/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { ICurrencyRate } from './i-currency-rate';
import { ICurrencyRates } from './i-currency-rates';
import { DataStorage } from '@studyportals/data-storage';

declare const CURRENCY_API_BASE_URL: string;

export class CurrencyRateRepository {
	private rates?: ICurrencyRates;
	private storageKey = 'CurrencyRateRepository/rates';
	private dataStorage = DataStorage;
	private isLoading = false;

	public async loadCurrencies(): Promise<ICurrencyRates> {
		if (this.rates) {
			return this.rates;
		}

		const currencyRateCache: string = this.dataStorage.retrieve(this.storageKey);
		if (currencyRateCache) {
			return JSON.parse(currencyRateCache) as ICurrencyRates;
		}

		if (this.isLoading) {
			await this.waitForRate();
			if (this.rates) {
				return this.rates;
			}
		}
		this.isLoading = true;

		try {
			this.rates = await this.getCurrencyRates();
			return this.rates;
		} finally {
			this.isLoading = false;
		}
	}

	private async getCurrencyRates(): Promise<ICurrencyRates> {
		const currencyRates = await this.getRates();
		const document = currencyRates?.document;

		const ttl = this.calculateTTL(document?.next_update as string);
		const source = document?.source;

		this.rates = {
			source,
			rates: this.formatCurrencyRates(document?.currencies)
		};

		this.dataStorage.store(this.storageKey, JSON.stringify(this.rates), ttl);

		return this.rates;
	}

	private async getRates(attempt = 1, delay = 1000): Promise<any> {
		const url = `${CURRENCY_API_BASE_URL}/rates`;
		try {
			const response = await fetch(url, { method: 'get' });
			const json = await response.json();
			return json;
		} catch (error) {
			if (attempt < 3) {
				const jitter = Math.random() * 1000;
				const backoffDelay = delay * 2 ** attempt + jitter;

				await new Promise(resolve => setTimeout(resolve, backoffDelay));
				return this.getRates(attempt + 1, backoffDelay);
			} else {
				throw error;
			}
		}
	}

	private calculateTTL(next_update: string): number {
		const ttl = Number(next_update) - new Date().getTime();
		return ttl / 1000;
	}

	private formatCurrencyRates(currencies: any): { [iso: string]: ICurrencyRate } {
		const rates: { [iso: string]: ICurrencyRate } = {};
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		for (const isoCode of Object.keys(currencies)) {
			const currencyRate = currencies[isoCode];

			rates[isoCode] = {
				target: isoCode,
				rate: currencyRate.quote,
				name: currencyRate.name
			};
		}

		return rates;
	}

	private async waitForRate(): Promise<void> {
		await new Promise<void>((resolve) => {
			const interval = setInterval(() => {
				if (!this.isLoading) {
					clearInterval(interval);
					resolve();
				}
			}, 100);
		});
	}
}
